import React, { useState } from 'react';
import axios from 'axios';
import { FaEnvelope, FaPhone } from 'react-icons/fa';
import impactraLogo from './logo.png'; // Ensure this path is correct

const boxShadowStyle = {
  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
};

const Header = () => (
  <header className="bg-white" style={boxShadowStyle}>
    <nav className="container mx-auto px-4 py-3 flex justify-between items-center">
      <div className="flex items-center">
        <img src={impactraLogo} alt="Impactra Logo" className="w-36 mr-2" />
        <span className="text-sm bg-blue-600 text-white px-2 py-1 rounded">beta</span>
      </div>
      <div>
        <a href="https://impactra.vercel.app" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-blue-600 mx-2 font-bold">About</a>
        <a href="#contact" className="text-gray-600 hover:text-blue-600 mx-2 font-bold">Contact</a>
      </div>
    </nav>
  </header>
);

const Hero = ({ handleAnalyze }) => (
  <div className="bg-blue-50 py-16">
    <div className="container mx-auto px-4">
      <div className="flex flex-wrap items-center justify-around">
        <div className="w-full lg:w-5/12 mb-8 lg:mb-0">
          <h2 className="text-4xl font-bold text-blue-800 mb-4">Performance Analyzer</h2>
          <h3 className="text-2xl font-semibold text-blue-600 mb-4">Track Developer's Impact factor and Rank Top Performers</h3>
          <p className="text-lg text-gray-600">Gain insights into contributor performance and impact</p>
        </div>
        <div className="w-full lg:w-5/12">
          <AnalysisForm onAnalyze={handleAnalyze} />
        </div>
      </div>
    </div>
  </div>
);

const AnalysisForm = ({ onAnalyze }) => {
  const [owner, setOwner] = useState('');
  const [repo, setRepo] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onAnalyze({ owner, repo });
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded px-8 pt-6 pb-8" style={boxShadowStyle}>
      <div className="mb-4">
        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="owner">
          Github Repository Owner:
        </label>
        <input
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          id="owner"
          type="text"
          placeholder="e.g. MunGell"
          value={owner}
          onChange={(e) => setOwner(e.target.value)}
          required
        />
      </div>
      <div className="mb-6">
        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="repo">
          Github Repository Name:
        </label>
        <input
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          id="repo"
          type="text"
          placeholder="e.g. awesome-for-beginners"
          value={repo}
          onChange={(e) => setRepo(e.target.value)}
          required
        />
      </div>
      <div className="flex items-center justify-between">
        <button
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          type="submit"
        >
          Analyze Repository
        </button>
      </div>
    </form>
  );
};

const ProductInfo = () => (
  <div className="bg-white py-16">
    <div className="container mx-auto px-4">
      <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">What is GitHub Performance Analyzer?</h2>
      <div className="max-w-3xl mx-auto text-center mb-12">
        <p className="text-lg text-gray-600 mb-6">
          Our AI tool analyzes Developer's Performance utilizing their Code, Code review comments and Business Analytics, helping you identify top performers and areas for improvement.
        </p>
      </div>
      <div className="bg-blue-50 p-8 rounded-lg max-w-2xl mx-auto" style={boxShadowStyle}>
        <h3 className="text-xl font-semibold mb-4">Important Assumptions:</h3>
        <ul className="list-disc pl-6 space-y-2 text-gray-700">
          <li>The repository must not be a private and have a README.md file</li>
          <li>This demo product utilizes code and comments analysis, without advanced Business Analytics.</li>
          <li>Designed for repositories that are not excessively large.</li>
          <li>Analysis is limited to the top 25 contributors or 10% of total contributors, whichever is smaller.</li>
          <li>Focus is on top performers, not least performers.</li>
          <li>This is a demonstration. The full product would use more advanced algorithms and Business Analytics.</li>
        </ul>
      </div>
    </div>
  </div>
);

const AnalysisResult = ({ results, totalContributors, analyzedContributors }) => {
  if (!Array.isArray(results) || results.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <p className="text-xl text-gray-600">No results to display.</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h2 className="text-2xl font-bold mb-4">Top Performers Analysis</h2>
      <p className="text-gray-600 mb-4">
        Analyzed top {analyzedContributors} out of {totalContributors} total contributors
      </p>
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white" style={boxShadowStyle}>
          <thead className="bg-gray-100">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Author</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quality</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Impact</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Difficulty</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contributions</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Score</th>
            </tr>
          </thead>
          <tbody>
            {results.map((result, index) => (
              <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{result.author}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{result.quality}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{result.impact}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{result.difficulty}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{result.contributions}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{result.totalScore}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Contact = () => (
  <div id="contact" className="bg-gray-100 py-16">
    <div className="container mx-auto px-4">
      <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">Contact Us</h2>
      <div className="flex justify-center space-x-8">
        <a href="mailto:impactra.ai@gmail.com" className="flex items-center text-blue-600 hover:text-blue-800 font-semibold">
          <FaEnvelope className="mr-2" />
          Email Us
        </a>
        <a href="https://calendly.com/harsh-23bcs10087-sst/30min?month=2024-08" target="_blank" rel="noopener noreferrer" className="flex items-center text-blue-600 hover:text-blue-800 font-semibold">
          <FaPhone className="mr-2" />
          Schedule a Call
        </a>
      </div>
    </div>
  </div>
);

const Footer = () => (
  <footer className="bg-gray-200 py-6">
    <div className="container mx-auto px-4">
      <p className="text-center text-gray-600">© 2024 Impactra. All rights reserved.</p>
    </div>
  </footer>
);

const App = () => {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalContributors, setTotalContributors] = useState(0);
  const [analyzedContributors, setAnalyzedContributors] = useState(0);

  const handleAnalyze = async ({ owner, repo }) => {
    setLoading(true);
    setError(null);
    try {
      console.log('Sending request to:', 'https://impactra.onrender.com/analyze');
      console.log('Request payload:', { owner, repo });
      const response = await axios.post('https://impactra.onrender.com/analyze', { owner, repo });
      console.log('Response received:', response.data);
      setResults(response.data.rankedEngineers);
      setTotalContributors(response.data.totalContributors);
      setAnalyzedContributors(response.data.analyzedContributors);
    } catch (error) {
      console.error('Error fetching analysis results:', error.response ? error.response.data : error.message);
      setError('An error occurred while analyzing the repository. Please check the console for more details.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-grow">
        <Hero handleAnalyze={handleAnalyze} />
        <ProductInfo />
        {loading && (
          <div className="text-center py-4">
            <p className="text-xl">Analyzing repository...</p>
          </div>
        )}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
            <strong className="font-bold">Error: </strong>
            <span className="block sm:inline">{error}</span>
          </div>
        )}
        {results && (
          <AnalysisResult
            results={results}
            totalContributors={totalContributors}
            analyzedContributors={analyzedContributors}
          />
        )}
        <Contact />
      </main>
      <Footer />
    </div>
  );
};

export default App;
